import { Box, Table, TableContainer, TablePagination } from '@material-ui/core';
import { AgentInLenderContext, Lender, PaymentMethodType, Pricing } from '@nemuru-eng/js-api-client';
import React, { useState } from 'react';
import { SWRConfig } from 'swr';

import { PricingData } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/types/PricingData';
import { CustomHeader } from '@/components/shared/components/CustomTable/CustomHeader';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { PricingActionsRowDialog } from '@/components/shared/components/PricingsTable/components/PricingActionsRowDialog/PricingActionsRowDialog';
import {
  PricingTableRow,
  SelectionProps,
} from '@/components/shared/components/PricingsTable/components/PricingTableRow';
import { usePricingsTable } from '@/components/shared/components/PricingsTable/hooks/usePricingsTable';
import { PricingRowActionType } from '@/components/shared/components/PricingsTable/types/PricingRowActionType';
import { useDefaultTable } from '@/components/shared/hooks/useDefaultTable';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { PendingDataType } from '@/config/types';

interface PricingsTableProps {
  pricings: (Pricing | PricingData)[] | undefined;
  lenders: Lender[];
  page: number;
  setPage: (page: number) => void;
  refreshData: () => Promise<unknown>;
  agentInLenderContext?: AgentInLenderContext[];
  defaultRowsPerPage?: number;
  selectPricing?: (pricingId: string) => void;
  editModeAllowed?: boolean;
  allowedRemovePricingFromAgent?: boolean;
  forceEnabledRow?: boolean;
}

export const PricingsTable = ({
  pricings,
  lenders,
  page,
  setPage,
  refreshData,
  agentInLenderContext,
  defaultRowsPerPage = 10,
  selectPricing = undefined,
  editModeAllowed = false,
  allowedRemovePricingFromAgent = false,
  forceEnabledRow = false,
}: PricingsTableProps) => {
  const tableElementId = 'pricing-table-container';
  const { opened, toggleDialog, setOpened } = useDialog();
  const { classes, stickyCellStyle } = useDefaultTable({
    tableElementId,
  });

  const paymentMethodTypes = !pricings
    ? []
    : [...new Set(pricings.map((pricing) => pricing.paymentMethodTypes).flat())];

  const [selectedPricing, setSelectedPricing] = useState(undefined as Pricing | undefined);
  const [rowPricingAction, setRowPricingAction] = useState(undefined as PricingRowActionType | undefined);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const showInsurances = paymentMethodTypes.includes(PaymentMethodType.AUTO_LOAN);

  const { columns } = usePricingsTable({ minimalTable: false, showInsurances });

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowPricingAction = (pricing: Pricing, pricingAction: PricingRowActionType) => {
    setSelectedPricing(pricing);
    setRowPricingAction(pricingAction);
    toggleDialog();
  };

  return (
    <>
      <TableContainer id={tableElementId}>
        <Table>
          <CustomHeader tableProps={{ classes, stickyCellStyle }} columns={columns} />
          {!pricings || pricings?.length === 0 ? (
            <Box position={'absolute'} marginY={2} height={'30px'} width={'100%'}>
              <PendingDataWarning type={PendingDataType.NO_PRICINGS_AVAILABLE} />
            </Box>
          ) : (
            pricings
              .sort((a, b) => (a.name > b.name ? 1 : -1))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((pricing, i) => {
                let selectionProps: SelectionProps | undefined;
                if ('selected' in pricing && selectPricing) {
                  selectionProps = {
                    isSelected: pricing.selected,
                    setIsSelected: selectPricing,
                  };
                }
                return (
                  <PricingTableRow
                    key={`${pricing.id}-${i}`}
                    pricing={pricing}
                    lenders={lenders}
                    showInsurances={showInsurances}
                    handleRowPricingAction={handleRowPricingAction}
                    agentInLenderContext={agentInLenderContext}
                    selectionProps={selectionProps}
                    editModeAllowed={editModeAllowed}
                    allowedRemovePricingFromAgent={allowedRemovePricingFromAgent}
                    forceEnabledRow={forceEnabledRow}
                  />
                );
              })
          )}
          {selectedPricing && rowPricingAction && (
            <SWRConfig value={{ revalidateOnMount: false }}>
              <PricingActionsRowDialog
                selectedPricing={selectedPricing}
                pricingAction={rowPricingAction}
                opened={opened}
                setOpened={setOpened}
                toggleDialog={toggleDialog}
                setRowPricingAction={setRowPricingAction}
                refreshData={refreshData}
                editModeAllowed={editModeAllowed}
              />
            </SWRConfig>
          )}
        </Table>
      </TableContainer>
      {pricings && pricings.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={pricings.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </>
  );
};
